// TitleBarView — the craft-FREE render of a Title Bar (heading band). Kept pure
// (plain props, no @craftjs / redux / hooks) so it is DOM-testable in isolation,
// exactly like CarouselView. The craft/redux adapter (index.jsx) resolves the
// background CSS + the solid-color-for-contrast and passes them down here.
//
// Deck (source of truth): concepts/builder-blocks/15-titlebar-block-editor.html
// (round-7 v3). A standalone plain rectangle spanning the Segment content width
// (2px corners, no chevron/pill, nothing expands) with a single heading whose
// size/font/alignment/tone the author chose. States handled:
//   • empty (builder)     -> a muted italic placeholder so the block is visible.
//   • empty (published)   -> the band paints its background with NO heading node
//                            (never an empty, name-less heading — an a11y trap).
//   • overflow/long title -> the heading WRAPS (band grows); text never clips.
// Motion: none (a static band) — nothing to gate under reduced-motion.
//
// DECK-15 DELTA (flag TITLE_BAR_DECK15_ENABLED, passed down as `deck15`): corner
// rounding, bar width, the 3x3 text position, overlay scope + Darken<->Lighten,
// and the Custom height / text-size escape hatches. Every one of those branches
// is inert when `deck15` is false, so the flag-OFF band is byte-identical. The
// same applies to `cropStyle` (crop-to-bar) and to a Patterns background, which
// arrives already resolved as part of `backgroundCss`.

import React from "react";
import { fontFamily as tokenFontFamily } from "../../../../theme/tokens";
import {
  sizeToPx,
  heightToPx,
  alignToJustify,
  alignToTextAlign,
  pickTextStyle,
  TITLE_WEIGHT,
  BAR_CORNER_RADIUS,
} from "./titleBarLogic";
import {
  CUSTOM,
  roundingToPx,
  widthToCss,
  positionToStyle,
  clampCustomHeight,
  clampCustomSize,
  resolveOverlay,
  contrastSolidFor,
} from "./titleBarDeck15";

const TitleBarView = ({
  title = "",
  backgroundCss = "#F3ECFF",
  solidForContrast = null,
  textTone = "auto",
  align = "center",
  size = "m",
  fontFamily,
  height = "m",
  editing = false,
  emptyLabel = "Title bar — add a heading",
  // ── DECK-15 DELTA (only supplied when TITLE_BAR_DECK15_ENABLED) ────────────
  // `deck15` false => every branch below is skipped and the band renders exactly
  // as the shipped block does. Nothing here changes the flag-OFF markup.
  deck15 = false,
  cornerRounding = "m",
  barWidth = "full",
  position = "middle-center",
  overlayScope = "none",
  overlayAdjust = 0,
  customHeightPx,
  customSizePx,
  // Crop-to-bar: a ready background-size/position/repeat triple from
  // barCrop.cropToBackgroundStyle, or null when the whole image is used.
  cropStyle = null,
}) => {
  const hasTitle = typeof title === "string" && title.trim().length > 0;
  const showPlaceholder = !hasTitle && editing;

  const overlay = deck15
    ? resolveOverlay(overlayScope, overlayAdjust)
    : { barTint: null, textBlock: null, textShadow: null };

  // A WHOLE-BAR tint changes what actually sits behind the title, so AUTO tone
  // must sample the tinted color — otherwise darkening a pale bar to -80 would
  // keep the title navy on near-black.
  const effectiveSolid = deck15
    ? contrastSolidFor(overlayScope, overlayAdjust, solidForContrast)
    : solidForContrast;

  const { color, textShadow } = pickTextStyle(textTone, effectiveSolid);
  const placement = deck15 ? positionToStyle(position) : null;
  const barRadius = deck15 ? roundingToPx(cornerRounding) : BAR_CORNER_RADIUS;

  const bandStyle = {
    width: deck15 ? widthToCss(barWidth) : "100%",
    boxSizing: "border-box",
    minHeight:
      deck15 && height === CUSTOM
        ? clampCustomHeight(customHeightPx)
        : heightToPx(height),
    display: "flex",
    alignItems: placement ? placement.alignItems : "center",
    justifyContent: placement ? placement.justifyContent : alignToJustify(align),
    padding: "8px 16px",
    background: backgroundCss,
    borderRadius: barRadius,
    // A narrowed band (75% / 50%) centers in the Segment content width rather
    // than hugging the left edge.
    ...(deck15 && barWidth !== "full" ? { margin: "0 auto" } : {}),
    ...(overlay.barTint ? { position: "relative", overflow: "hidden" } : {}),
    // Crop-to-bar zooms the background to the author's region. Applied AFTER
    // `background` so the shorthand cannot reset the size/position it sets.
    ...(deck15 && cropStyle ? cropStyle : {}),
  };

  const headingStyle = {
    margin: 0,
    width: "100%",
    fontFamily: fontFamily || tokenFontFamily,
    fontSize:
      deck15 && size === CUSTOM ? clampCustomSize(customSizePx) : sizeToPx(size),
    fontWeight: TITLE_WEIGHT,
    lineHeight: 1.3,
    color,
    // "Shadow behind text" replaces the AUTO scrim with the author's own soft
    // shadow; every other scope keeps the legibility scrim untouched.
    textShadow: overlay.textShadow || textShadow,
    textAlign: placement ? placement.textAlign : alignToTextAlign(align),
    // Long titles wrap (the band grows) rather than clipping/overflowing.
    overflowWrap: "anywhere",
    wordBreak: "break-word",
    // "Block behind text": a tinted block that HUGS the heading and rounds with
    // the bar's own Corner rounding (deck note), so it never spans the band.
    ...(overlay.textBlock
      ? {
          width: "auto",
          maxWidth: "100%",
          padding: "4px 10px",
          background: overlay.textBlock,
          borderRadius: barRadius,
        }
      : {}),
    // The heading sits ABOVE a whole-bar tint layer.
    ...(overlay.barTint ? { position: "relative", zIndex: 1 } : {}),
  };

  const placeholderStyle = {
    ...headingStyle,
    opacity: 0.6,
    fontStyle: "italic",
    fontWeight: 500,
  };

  return (
    <div style={bandStyle} data-title-bar aria-label="Title bar">
      {overlay.barTint ? (
        // Decorative tint layer for the "Whole bar" scope — a real element
        // rather than CSS-string surgery on `background`, so an image/gradient
        // fill keeps every one of its own layers intact.
        <div
          aria-hidden="true"
          data-title-bar-tint
          data-testid="title-bar-tint"
          style={{
            position: "absolute",
            inset: 0,
            background: overlay.barTint,
            pointerEvents: "none",
          }}
        />
      ) : null}
      {hasTitle ? (
        <span role="heading" aria-level={2} style={headingStyle} data-title-bar-heading>
          {title}
        </span>
      ) : showPlaceholder ? (
        <span style={placeholderStyle} data-title-bar-placeholder>
          {emptyLabel}
        </span>
      ) : null}
    </div>
  );
};

export default TitleBarView;
